import { createFileRoute } from "@tanstack/react-router";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Loader2, Plus, RefreshCw, UsersIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pagination } from "@/components/ui/pagination";
import { SearchInput } from "@/components/ui/search-input";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { useServerPagination } from "@/hooks/useServerPagination";
import { employeesApi, type Employee } from "@/services/api/hr";

export const Route = createFileRoute("/portal/hr/employees/")({
  component: EmployeesPage,
});

type SortKey = "name" | "code" | "created_at_utc";

function formatDate(value: string | null): string {
  if (!value) return "—";
  const iso = /[zZ]|[+-]\d{2}:\d{2}$/.test(value) ? value : `${value}Z`;
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function EmployeesPage() {
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const orgId = selectedOrganisation?.organisationId ?? "";
  const queryClient = useQueryClient();

  const {
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    offset,
    searchTerm,
    setSearchTerm,
    getPaginationInfo,
  } = useServerPagination({ initialItemsPerPage: 25 });

  const [sortBy, setSortBy] = useState<SortKey>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Reset page + search when the org changes, at render time so the query
  // observer never fires a stale-offset request against the new org.
  const [prevOrg, setPrevOrg] = useState(orgId);
  if (prevOrg !== orgId) {
    setPrevOrg(orgId);
    setCurrentPage(1);
    setSearchTerm("");
  }

  const search = searchTerm.trim();

  const list = useQuery({
    queryKey: ["hr-employees", orgId, offset, itemsPerPage, search, sortBy, sortOrder],
    queryFn: () =>
      employeesApi.list(token, {
        offset,
        limit: itemsPerPage,
        search,
        sort: sortBy,
        order: sortOrder,
      }),
    enabled: !!token && !!orgId,
    // Keep the current page visible while the next page/sort loads — no spinner
    // flash and the Pagination bar doesn't unmount mid-click.
    placeholderData: keepPreviousData,
  });

  const rows: Employee[] = list.data?.items ?? [];
  const total = list.data?.total ?? 0;
  const { totalPages, startIndex, endIndex } = getPaginationInfo(total);
  const canGoPrev = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  // Clamp if the result set shrank under us (e.g. a concurrent delete leaves us
  // on a now-out-of-range page).
  useEffect(() => {
    if (list.data && totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [list.data, totalPages, currentPage, setCurrentPage]);

  function handleSort(key: SortKey) {
    if (sortBy === key) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  }

  const getSortDirection = (key: SortKey): "asc" | "desc" | null =>
    sortBy === key ? sortOrder : null;

  const onSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1); // a narrower result set may have fewer pages
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">Employees</h1>
          <p className="mt-2 text-lg text-slate-600">
            Your payroll employee directory, synced from onboarding and cost data.
          </p>
        </div>
        <CreateEmployeeDialog
          onCreated={() =>
            queryClient.invalidateQueries({ queryKey: ["hr-employees"] })
          }
        />
      </div>

      <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-6">
        {/* Controls */}
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            value={searchTerm}
            onChange={(e) => onSearch(e.target.value)}
            onClear={() => onSearch("")}
            placeholder="Search by name or code…"
            containerClassName="flex-1 min-w-[200px]"
            data-testid="employee-search"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => list.refetch()}
            disabled={list.isFetching}
          >
            {list.isFetching ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>

        {list.isError && (
          <p className="text-sm text-red-600">Failed to load employees.</p>
        )}

        {list.isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : rows.length === 0 && !list.isError ? (
          <div className="text-center py-12">
            <UsersIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {search ? "No employees match your search" : "No employees yet"}
            </h3>
            <p className="text-muted-foreground">
              {search
                ? "Try a different search term."
                : "Add an employee to get started."}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <SortableTableHead
                  direction={getSortDirection("code")}
                  onSort={() => handleSort("code")}
                >
                  Code
                </SortableTableHead>
                <SortableTableHead
                  direction={getSortDirection("name")}
                  onSort={() => handleSort("name")}
                >
                  Name
                </SortableTableHead>
                <SortableTableHead
                  direction={getSortDirection("created_at_utc")}
                  onSort={() => handleSort("created_at_utc")}
                >
                  Added
                </SortableTableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((emp) => (
                <TableRow key={emp.id} data-testid={`employee-${emp.id}`}>
                  <TableCell className="font-mono text-sm">{emp.code}</TableCell>
                  <TableCell className="font-medium">{emp.name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(emp.created_at_utc)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {total > 0 && (
          <div className="mt-4 border-t pt-4">
            <Pagination
              page={currentPage}
              totalPages={totalPages}
              totalCount={total}
              pageSize={itemsPerPage}
              pageSizeOptions={[10, 25, 50]}
              startIndex={startIndex}
              endIndex={endIndex}
              canGoPrev={canGoPrev}
              canGoNext={canGoNext}
              onPageChange={setCurrentPage}
              onPageSizeChange={setItemsPerPage}
              onFirstPage={() => setCurrentPage(1)}
              onLastPage={() => setCurrentPage(totalPages)}
              onPrevPage={() => setCurrentPage(currentPage - 1)}
              onNextPage={() => setCurrentPage(currentPage + 1)}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function CreateEmployeeDialog({ onCreated }: { onCreated: () => void }) {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");

  const create = useMutation({
    mutationFn: () => employeesApi.create(token, { code: code.trim(), name: name.trim() }),
    onSuccess: () => {
      toast.success("Employee added");
      setOpen(false);
      setCode("");
      setName("");
      onCreated();
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Create failed"),
  });

  const canSubmit = code.trim().length > 0 && name.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-full" data-testid="add-directory-employee">
          <Plus className="h-4 w-4 mr-2" />
          Add employee
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add employee</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="employee-code">Employee code</Label>
            <Input
              id="employee-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="e.g. EMP001"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="employee-name">Name</Label>
            <Input
              id="employee-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={() => create.mutate()}
            disabled={!canSubmit || create.isPending}
            data-testid="create-directory-employee-submit"
          >
            {create.isPending ? "Adding…" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
